import {DigitalSigningDetailsView, DigitalSigningStatusView} from "../digitalSigning.model";
import {formatDuration, formatMoney} from "../public/format.util";
import type {SignedSignature, SignedTip} from "../public/DigitalSigningSignedView";

/** Returns a friendly device label (e.g. "iPad", "iPhone") from a user agent string. */
export const deviceLabel = (userAgent?: string): string | undefined => {
    const agent = userAgent?.trim();
    if (!agent) return undefined;

    if (/iPad/i.test(agent)) return 'iPad';
    if (/iPhone|iPod/i.test(agent)) return 'iPhone';
    if (/Android/i.test(agent)) return /Mobile/i.test(agent) ? 'Android phone' : 'Android tablet';
    if (/CrOS/i.test(agent)) return 'Chromebook';
    if (/Windows/i.test(agent)) return 'Windows';
    if (/Macintosh|Mac OS X/i.test(agent)) return 'Mac';
    if (/Linux/i.test(agent)) return 'Linux';
    return undefined;
};

/** Extracts tip and signature info for the signed view once the guest has signed. */
export const signedPageOf = (details: DigitalSigningDetailsView): {tip?: SignedTip, signature?: SignedSignature} => (
    details.signedAt || details.signature
        ? {
            tip: details.tip,
            signature: {
                imageUrl: details.signature?.imageUrl ?? details.signatureUrl,
                signedAt: details.signature?.signedAt ?? details.signedAt,
                // The page in the language the guest signed in
                language: details.signature?.language,
            },
        }
        : {}
);

/** "Aromaölmassage · 50 Min. · 120,00 EUR": the service, its length and the bill before the tip - what is known of them. */
export const serviceSummary = (
    signing: Pick<DigitalSigningStatusView, 'serviceName' | 'durationMinutes' | 'totalCents' | 'currency'>,
    language: string
): string => [
    signing.serviceName?.trim(),
    signing.durationMinutes ? formatDuration(signing.durationMinutes, language) : undefined,
    // Staff read the currency's code, like in the folio dialog
    signing.totalCents != null ? formatMoney(signing.totalCents, signing.currency, language, false, 'code') : undefined,
].filter(Boolean).join(' · ');
